import { ChangeEvent, useState, useRef } from 'react';
import { toast } from 'react-hot-toast';
import useGetAudioSchema from '../../../../API/hooks/devices/useGetAudioSchema';
import useGetFleetId from '../../../../API/hooks/useGetFleetId';
import useGetRole from '../../../../API/hooks/useGetRole';
import { AudioSchema } from '../../../../API/types/AudioSchemas';
import { Button } from '../../../../components/ui/Button/Button';
import { ALERT_AUDIO_NAMES } from '../../../../constants/AlertConfig';
import S from './AlertSchemaItemStyles';
import DeleteModal from '../../../../components/common/DeleteModal/DeleteModal';
import Modal from '../../../../components/common/Modal/Modal';
import usePatchAudioSchemaFile from '../../../../API/hooks/devices/usePatchAudioSchemaFile';
import Input from '../../../../components/common/Input/Input';
import { logger } from '../../../../API/services/SharedService';

type Props = {
  audioBundleId?: string;
  audioKey: string;
  label: string;
  audio?: string;
  readOnly?: boolean;
};

const MAX_FILE_SIZE = 150;
const MAX_DURATION_SEC = 10;

function readAudioDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    const cleanup = (): void => {
      URL.revokeObjectURL(url);
      audio.removeAttribute('src');
      audio.load();
    };
    audio.onloadedmetadata = (): void => {
      const { duration } = audio;
      cleanup();
      resolve(duration);
    };
    audio.onerror = (): void => {
      cleanup();
      reject(new Error('metadata'));
    };
    audio.src = url;
  });
}

function AlertSchemaItem({
  audioBundleId = '', audioKey, label, audio, readOnly = false,
}: Props): JSX.Element {
  const fleetId = useGetFleetId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [audioPreview, setAudioPreview] = useState<any>(audio);
  const [audioFile, setAudioFile] = useState<any>(undefined);
  const [modalDeleteFile, toggleModalDeleteFile] = useState(false);
  const onUpdateSuccess = (): any => {
    toast.success('Custom audio file was updated');
    setAudioFile(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const onUpdateFail = (errorMessage: string): any => {
    toast.error(`Custom audio file updating failed: ${errorMessage}`);
  };
  const onDeleteSuccess = (): any => {
    toast.success('Custom audio file was removed');
    toggleModalDeleteFile(false);
    setAudioFile(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const onDeleteFail = (errorMessage: string): any => {
    toast.error(`Custom audio file removing failed: ${errorMessage}`);
  };

  const { mutate: mutateUpdate } = usePatchAudioSchemaFile(onUpdateSuccess, onUpdateFail);
  const { mutate: mutateDelete } = usePatchAudioSchemaFile(onDeleteSuccess, onDeleteFail);

  const clearFileInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAudioChange = async (e: ChangeEvent<any>): Promise<void> => {
    const file = e.target.files[0];
    if (!file) {
      setAudioPreview(undefined);
      setAudioFile(undefined);
      logger.warning('AlertSchemaItem handleAudioChange', undefined);
      return;
    }
    try {
      const duration = await readAudioDurationSeconds(file);
      if (!Number.isFinite(duration) || duration > MAX_DURATION_SEC) {
        toast.error(
          `Only audio up to ${MAX_DURATION_SEC} sec is allowed${Number.isFinite(duration) ? ` (actually ${Math.ceil(duration)} sec)` : ''}`,
        );
        setAudioPreview(undefined);
        setAudioFile(undefined);
        clearFileInput();
        return;
      }
      const reader = new FileReader();
      reader.onload = function (event) {
        const audioDataURL = event?.target?.result;
        setAudioPreview(audioDataURL as string);
        setAudioFile(file);
        logger.info('AlertSchemaItem handleAudioChange', file);
      };
      reader.readAsDataURL(file);
    } catch {
      toast.error('Could not read audio duration');
      setAudioPreview(undefined);
      setAudioFile(undefined);
      clearFileInput();
    }
  };

  const handleUpdateFile = (): void => {
    if (audioFile.size > MAX_FILE_SIZE * 1024) {
      toast.error(`Maximum size ${MAX_FILE_SIZE}KB is allowed (actually ${Math.round(audioFile.size / 1024)}KB)`);
      return;
    }
    if (!['audio/aac', 'audio/vnd.dlna.adts'].includes(audioFile.type)) {
      toast.error(`Only AAC format is allowed (actually ${audioFile.type})`);
      return;
    }
    mutateUpdate({
      fleetId,
      audioBundleId,
      audioAlertName: audioKey,
      description: `Audio file for ${label}`,
      file: audioFile,
    });
  };

  const handleDeleteFile = (): void => mutateDelete({
    fleetId,
    audioBundleId,
    audioAlertName: audioKey,
    description: '',
    file: undefined,
  });

  return (
    <S.Wrapper>
      <div className="audio-row" key={audioKey}>
        <p className="name">{label}</p>
        <audio controls>
          <source src={audioPreview} type="audio/mpeg" />
          <track kind="captions" src="" label="English" />
          Your browser does not support the audio element.
        </audio>
        {!readOnly && (
          <>
            <Input
              type="file"
              name="file"
              ref={fileInputRef}
              styleWrapper={{ width: '300px', height: '40px' }}
              placeholder={`Select .aac file (max ${MAX_FILE_SIZE} KB, ${MAX_DURATION_SEC} sec)`}
              onChange={handleAudioChange}
              accept="audio/aac"
            />
            <button type="button" disabled={readOnly || !audioFile} onClick={() => handleUpdateFile()}>Upload</button>
            {/* <button type="button" disabled={readOnly} onClick={() => toggleModalDeleteFile(true)}>Reset</button> */}
          </>
        )}
      </div>
      {modalDeleteFile && (
        <Modal
          title="Delete audio file"
          state={modalDeleteFile}
          toggleState={toggleModalDeleteFile}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete this audio file? Default audio will be applied automaticaly."
            onClose={() => toggleModalDeleteFile(false)}
            onDelete={handleDeleteFile}
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default AlertSchemaItem;
